/**
 * Global merchant state model
 * Manages the currently selected merchant (partner) for multi-merchant users
 *
 * localStorage keys are user-specific to avoid cross-user data leakage:
 * - aimerpay_merchant_{userId} - selected merchant ID for user
 */

import { history } from '@umijs/max';
import { useCallback, useRef, useState } from 'react';
import { getPartners } from '@/services/business/partner';

// Helper to get user-specific storage key
const getStorageKey = (userId: string | undefined, suffix: string) => {
  return userId ? `aimerpay_${suffix}_${userId}` : `aimerpay_${suffix}`;
};

/**
 * Merchant info for UI display
 * Derived from BusinessAPI.Partner
 */
export interface MerchantInfo {
  id: number;
  partnerName: string;
  legalName?: string;
  type: 'organization' | 'individual';
  status: BusinessAPI.PartnerStatus;
  email: string;
  phone?: string;
  hasAdyenAccountHolder: boolean;
  balanceAccountCount: number;
}

/**
 * Convert BusinessAPI.Partner to MerchantInfo for UI
 */
function partnerToMerchantInfo(partner: BusinessAPI.Partner): MerchantInfo {
  return {
    id: partner.id,
    partnerName: partner.partnerName,
    legalName: partner.legalName,
    type: partner.type,
    status: partner.status,
    email: partner.email,
    phone: partner.phone,
    hasAdyenAccountHolder: partner.hasAdyenAccountHolder,
    balanceAccountCount: partner.balanceAccountCount,
  };
}

export default function useMerchantModel() {
  const [currentMerchant, setCurrentMerchant] = useState<MerchantInfo | null>(
    null,
  );
  const [merchants, setMerchants] = useState<MerchantInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const currentUserIdRef = useRef<string | undefined>(undefined);

  // Load merchants from API - called explicitly after login
  // userId is optional and will be used for user-specific storage
  const fetchMerchants = useCallback(async (userId?: string) => {
    setLoading(true);
    // Store userId for later use
    if (userId) {
      currentUserIdRef.current = userId;
    }
    const effectiveUserId = userId || currentUserIdRef.current;

    try {
      const response = await getPartners();
      // Handle Business API format: { success, message, data: Partner[] }
      const partners = response.data || [];

      if (partners.length > 0) {
        const merchantList: MerchantInfo[] = partners.map(
          partnerToMerchantInfo,
        );
        setMerchants(merchantList);

        // Save merchants list to localStorage for access.ts
        const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
        localStorage.setItem(
          merchantsListKey,
          JSON.stringify(merchantList.map((m) => m.id)),
        );

        // Set current merchant from localStorage or default to first
        const merchantKey = getStorageKey(effectiveUserId, 'merchant');
        const savedMerchantIdStr = localStorage.getItem(merchantKey);
        const savedMerchantId = savedMerchantIdStr
          ? Number.parseInt(savedMerchantIdStr, 10)
          : null;
        const savedMerchant = savedMerchantId
          ? merchantList.find((m) => m.id === savedMerchantId)
          : null;

        if (savedMerchant) {
          setCurrentMerchant(savedMerchant);
        } else if (merchantList.length > 0) {
          setCurrentMerchant(merchantList[0]);
          localStorage.setItem(merchantKey, String(merchantList[0].id));
        }

        return { hasMerchants: true, merchants: merchantList };
      }
      // No merchants available
      setMerchants([]);
      setCurrentMerchant(null);

      // Clear user-specific storage
      const merchantKey = getStorageKey(effectiveUserId, 'merchant');
      localStorage.removeItem(merchantKey);
      const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
      localStorage.removeItem(merchantsListKey);

      return { hasMerchants: false, merchants: [] };
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
      return { hasMerchants: false, merchants: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  // Change current merchant
  const changeMerchant = useCallback(
    (merchantId: number) => {
      const merchant = merchants.find((m) => m.id === merchantId);
      if (merchant) {
        setCurrentMerchant(merchant);

        // Save to user-specific localStorage
        const merchantKey = getStorageKey(currentUserIdRef.current, 'merchant');
        localStorage.setItem(merchantKey, String(merchantId));

        // Handle detail page redirects - only redirect from actual detail pages
        const currentPath = window.location.pathname;

        // Check for balance accounts detail page (e.g., /merchants/balance-accounts/123)
        if (currentPath.match(/^\/merchants\/balance-accounts\/\d+/)) {
          history.push('/merchants/balance-accounts');
          return;
        }

        // Check for partner detail page (e.g., /merchants/123) but NOT balance-accounts list
        const partnerDetailMatch = currentPath.match(/^\/merchants\/(\d+)$/);
        if (partnerDetailMatch) {
          const viewingPartnerId = Number.parseInt(partnerDetailMatch[1], 10);
          // If we're viewing the same partner we're switching to, stay on the page
          if (viewingPartnerId === merchantId) {
            return;
          }
          // Otherwise navigate to the new merchant's detail page
          history.push(`/merchants/${merchantId}`);
          return;
        }
      }
    },
    [merchants],
  );

  // Clear current merchant (for logout)
  const clearMerchant = useCallback(() => {
    // Clear user-specific storage
    const effectiveUserId = currentUserIdRef.current;
    if (effectiveUserId) {
      const merchantKey = getStorageKey(effectiveUserId, 'merchant');
      localStorage.removeItem(merchantKey);
      const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
      localStorage.removeItem(merchantsListKey);
    }

    // Reset state
    setCurrentMerchant(null);
    setMerchants([]);
    currentUserIdRef.current = undefined;
  }, []);

  // Add a new merchant and optionally switch to it
  const addMerchant = useCallback(
    (partner: BusinessAPI.Partner, switchTo: boolean = true) => {
      const newMerchant = partnerToMerchantInfo(partner);

      // Add to merchants list
      setMerchants((prev) => {
        // Check if already exists
        if (prev.some((m) => m.id === newMerchant.id)) {
          return prev;
        }
        return [...prev, newMerchant];
      });

      // Switch to the new merchant if requested
      if (switchTo) {
        setCurrentMerchant(newMerchant);
        const merchantKey = getStorageKey(currentUserIdRef.current, 'merchant');
        localStorage.setItem(merchantKey, String(newMerchant.id));
      }

      return newMerchant;
    },
    [],
  );

  // Load merchants directly (used by SuperAdmin merchant owner mode)
  const loadMerchants = useCallback(
    (merchantList: MerchantInfo[], userId?: string, selectId?: number) => {
      // Set userId ref so subsequent changeMerchant/clearMerchant use correct keys
      if (userId) {
        currentUserIdRef.current = userId;
      }
      setMerchants(merchantList);

      // Set current merchant directly to avoid stale closure in changeMerchant
      const target = selectId
        ? merchantList.find((m) => m.id === selectId)
        : merchantList[0];
      if (target) {
        setCurrentMerchant(target);
        const merchantKey = getStorageKey(
          userId || currentUserIdRef.current,
          'merchant',
        );
        localStorage.setItem(merchantKey, String(target.id));
      }

      const effectiveUserId = userId || currentUserIdRef.current;
      if (effectiveUserId) {
        const merchantsListKey = getStorageKey(effectiveUserId, 'merchants');
        localStorage.setItem(
          merchantsListKey,
          JSON.stringify(merchantList.map((m) => m.id)),
        );
      }
    },
    [],
  );

  return {
    currentMerchant,
    merchants,
    loading,
    fetchMerchants,
    changeMerchant,
    clearMerchant,
    addMerchant,
    loadMerchants,
  };
}
